import _ from 'lodash'
import i18n from 'scoreboard/components/i18n'
import ScorecardReportModel from 'scoreboard/reports/scorecard-report/scorecard-report-model'
import Dataset from 'scoreboard/datasets/model/dataset'
import isNumericDataType from 'scoreboard/components/is-numeric-data-type'
import isDateTimeDataType from 'scoreboard/components/is-date-time-data-type'
import unwrapModel from 'scoreboard/components/unwrap-model'
import hasRelativeDateFilter from 'scoreboard/datasets/has-relative-date-filter'
import wrapModel from 'scoreboard/components/wrap-model'
import * as AggregationTypeOptions from 'scoreboard/datasets/aggregation-type-options'
const { nonTotalColumn } = AggregationTypeOptions
import isGeographicOrRollup from '../../datasets/is-geographic-or-rollup'
import { datasetFieldsToOptions } from 'scoreboard/datasets/render-field-dropdown'
import { addLinkedDatasetLabelData } from 'scoreboard/reports/common/report-column-chooser'
import DataType from 'scoreboard/datasets/model/data-type'
const { DATE, DATE_TIME, DATASET_LINK, NUMBER } = DataType

const DatasetReportModel = ScorecardReportModel.extend({

	allowTemplate: false,
	designer: true,
	reportDomainType: 'dataset',
	fieldOrColumnTypeProperty: 'datasetFieldId',

	_setupListeners: function() {
		this.on('change:showIndividualRecords change:groupBy', () => {
			// when toggling showing individual records, need to apply correct
			// aggregation types
			_.each(this.allColumns(), column => {
				const fieldId = column.get('datasetFieldId')
				const vlfId = column.get('visibleLinkedFieldId')

				// #54312 make sure to get the vlf if necessary as
				// _fieldDataByDatasetFieldId will only have native field data
				const field = this._fieldDataByDatasetFieldId[fieldId] ??
					this.get('dataset')?.visibleLinkedFieldsById[vlfId]

				column.set('aggregationType', this._getColumnAggregationTypeForField(field))
				if (!this.isAggregating()) {
					// #37875 Column filters don't make sense if we're not aggregating
					column.set('datasetFilters', [])
				}
			})
		})

		//anytime the calendar field changes, reload the results
		this.on('change:calendarFieldId', () => {
			this.reload()
		})

		//this must go last as listeners are fired in the order they are added
		//and definitions should be create from narrowest scope (subclass)
		//to greatest scope (parent class) #42668
		ScorecardReportModel.prototype._setupListeners.apply(this, arguments)
	},

	/**
	 * return the aggregation type that should be in effect for columns backed
	 * by that field.
	 */
	_getColumnAggregationTypeForField: function(field,
		datasetModel = this.get('dataset'),
		aggregating = this.isAggregating()
	) {
		if (_.isNil(field)) { return 'none' }

		// if there are many values for the field for one row in the report,
		// we'll need an aggregation type
		const needsAggType = aggregating || !datasetModel.isOneSide(field.datasetId)
		if (needsAggType) {
			if (!_.isNil(field)) {
				const dataType = datasetModel.getUnderlyingField(field).dataType
				return _.get(nonTotalColumn(dataType), '[0].id')
			} else {
				return 'unique'
			}
		} else {
			return 'none'
		}
	},

	defaults() {
		return _.extend({
			subtype: 'dataset-report-designer',
			filters: [],
			structure: {
				stacks: []
			},
			calendarFieldId: ""
		}, this._defaultProperties())
	},

	validate(attrs = this.attributes, opts) {
		let errors = {}
		if (_.isEmpty(attrs.label)) {
			errors.label = i18n.get('common.error.required', i18n.get('report.title'))
		}
		let dataset = this.get('dataset')
		if (_.isEmpty(dataset) || _.isNil(dataset)) {
			errors.label = i18n.get('common.error.required', i18n.get('datasets.dataset'))
		}
		return this.formatErrors(errors, opts)
	},

	_parseDataset: function(attrs) {
		/** @type {Dataset} */
		let datasetModel = wrapModel(attrs.dataset, Dataset)

		let vlfsByFieldId = _.keyBy(datasetModel.visibleLinkedFieldsById, 'fieldId')
		this._fieldDataByDatasetFieldId = {}
		_.each(datasetModel.combinedFields, field => {
			let underlyingField = datasetModel.getUnderlyingField(field)
			let fieldData = _.extend({}, datasetModel.getFieldByFieldIdentifier(field.id))
			let { dataType } = underlyingField

			let vlf = vlfsByFieldId[field.id]
			let isNative = datasetModel.id == fieldData.datasetId
			if (isNative || vlf) {
				fieldData.fieldId = fieldData.id
				if (vlf) {
					fieldData.label = vlf.label
					// #39159 add VLF IDs to _fieldDataByDatasetFieldId. This doesn't
					// properly account for two VLFs being for the same field, so we'll
					// need to go in and clean this up later]
					// field will be undefined for "Total" a.k.a "Number of " fields
					fieldData.visibleLinkedFieldId = vlf.id
				}

				//https://github.com/SpiderStrategies/Scoreboard/issues/48107#issuecomment-1162958272
				//fieldData.changesOverTime = isDateTimeDataType(dataType)
				fieldData.changesOverTime = dataType == DATE || dataType == DATE_TIME
				fieldData.supportsHierarchyOrdering = isGeographicOrRollup(dataType)

				// if there are many values for the field for one row in the report,
				// we'll need an aggregation type
				fieldData.foreignFieldOnManySide = !datasetModel.isOneSide(field.datasetId)

				//Note; we cannot use this.isAggregating() function here because the
				//parsing of the dataset on the initial construction happens before
				//backbone sets the properties, so we must manually determine it
				let aggregating = !this.isShowingIndividualRecords(attrs) && this.isGrouping(attrs)

				fieldData.abbreviated = !!isNumericDataType(dataType)
				fieldData.aggregationType = this._getColumnAggregationTypeForField(fieldData, datasetModel, aggregating)
				fieldData.supportsIndentHierarchy = isGeographicOrRollup(fieldData.dataType) && fieldData.aggregationType == 'none'
				this._fieldDataByDatasetFieldId[fieldData.id] = fieldData
			}
		})
		return datasetModel
	},

	parse: function(attrs, opts) {
		if (attrs.dataset) {
			attrs.dataset = this._parseDataset(attrs)
		}
		if (attrs.treeItems) {
			this.treeItemsById = _.keyBy(attrs.treeItems, 'id')
		}

		return attrs
	},

	_parseForm: function(attrs) {
		//make sure we're working with a dataset Model on the client
		this.set('dataset', this._parseDataset(attrs), {silent:true})
		this.treeItemsById = _.keyBy(attrs.treeItems || [], 'id')

		//ensure for new reports, we set the security filters returned from the server
		this.set('securityFilters', attrs.securityFilters || [])

		ScorecardReportModel.prototype._parseForm.call(this, attrs)
	},

	/**
	 * Unlike Scorecards/Initiatives, all fields on a Dataset report are always
	 * available to be used in any scenario
	 */
	_optionList: function(opts = {}) {
		let dataset = this.get('dataset')
		let allowedFieldIds = _.map(dataset.get('fields'), f => f.id)

		// #54632 filter out number of fields if this is a repeat by menu
		let visibleLinkedFields = _.values(dataset.visibleLinkedFieldsById)
		if (opts.asRepeatBy) {
			visibleLinkedFields = _.filter(visibleLinkedFields, f =>
				f.fieldId != Dataset.TOTAL)
		}
		let allowedVisibleLinkedFieldIds = _.map(visibleLinkedFields, f => f.id)

		//#45654 ensure dataset options are consistent across app
		let optionData = datasetFieldsToOptions(dataset, _.extend({
			allowedVisibleLinkedFieldIds,
			allowedFieldIds,
			includeNativeTotal: true
		}, opts))
		addLinkedDatasetLabelData(optionData)
		return optionData.options
	},

	repeatByColumnOptionsList: function(stack, opts = {}) {
		return this._optionList(opts)
	},

	nonRepeatByColumnOptionsList: function(stack, opts = {}) {
		return this._optionList()
	},

	/**
	 * Deliberately does nothing; No groups for dataset field select
	 *
	 * @override
	 * **/
	_assignOptionGroup: function(opt) {},

	/**
	 * Set any additional filters to be added into the report (eg filter widgets
	 * on a dashboard)
	 */
	liveFilters: function (filters) {
		this._liveFilters = filters
	},

	toJSON: function() {
		let obj = _.defaults({
			//we only need to send id of the dataset
			dataset: {id: this.get('dataset').id }
		}, this.attributes)

		if (this.liveFilters) {
			obj.filters = _.concat(obj.filters || [], this._liveFilters || [])
		}

		return obj
	},

	_defaultColumnData: function(fieldIdentifier, overrides = {}) {
		if (_.isNil(fieldIdentifier)) { return null }
		let dataset = this.attributes.dataset
		if (_.isString(fieldIdentifier)) {
			//ensure we're working with numbers
			fieldIdentifier = _.parseInt(fieldIdentifier)
		}

		let fieldId = fieldIdentifier
		//the fieldIdentifier could be a negative from option data
		if (Dataset.isVisibleLinkedFieldIdentifier(fieldIdentifier)) {
			let fieldIdentifierData = dataset.fieldIdentifierToIds(fieldIdentifier)
			if (_.isNil(fieldIdentifierData)) {
				return null
			}
			fieldId = fieldIdentifierData.fieldId

			if (Dataset.isTotal(fieldId)) {
				const vlf = dataset.visibleLinkedFieldsById[fieldIdentifierData.visibleLinkedFieldId]
				overrides.visibleLinkedFieldId = fieldIdentifierData.visibleLinkedFieldId
				overrides.label = vlf.label
				overrides.dataType = NUMBER
				overrides.aggregationType = 'sum'
			}
		}

		let field = this._fieldDataByDatasetFieldId[fieldId]
		if (Dataset.isTotal(fieldIdentifier)) {
			field = dataset.getFieldByFieldIdentifier(fieldIdentifier, dataset.id)
			field.aggregationType = 'sum'
		}

		if (!overrides.aggregationType) {
			//if overrides does not define an agg type, keep default
			delete overrides.aggregationType
		}

		let columnData =_.defaults({
			id: null,
			datasetFieldId: fieldId,
			summaryType: 'value'
		}, field)

		return _.extend(columnData, overrides)
	},

	defaultBreakdownDataByFieldIdOrColumnType: function(fieldIdOrColumnType) {
		let data = {}
		//the breakdown should point to the fieldId and if present the VLF ID
		let dataset = this.get('dataset')
		let fieldData = dataset.fieldIdentifierToIds(fieldIdOrColumnType)
		if (fieldData) {
			let {fieldId, visibleLinkedFieldId} = fieldData
			data.breakdownBy = fieldId
			data.breakdownByVisibleLinkedFieldId = visibleLinkedFieldId
		}
		return data
	},

	getDefaultRepeatByLabel: function(column) {
		return _.get(this.getDatasetField(column), 'label')
	},

	_columnDataMap: function() {
		return this._fieldDataByDatasetFieldId || {}
	},

	/** @override */
	getSummaryTypeOptionsList: function(columnOrBreakdown) {
		let summaryTypeOptions = ScorecardReportModel.prototype
			.getSummaryTypeOptionsList.apply(this, arguments)
		const datasetField = this.getDatasetField(columnOrBreakdown)

		// #39327 Don't show range option if there are no categories/ranges
		if (!datasetField || _.isEmpty(datasetField.ranges)) {
			summaryTypeOptions = _.filter(summaryTypeOptions, opt => opt.id != 'range')
		}
		return summaryTypeOptions
	},

	_buildNonDateColumnSummaryTypeOptions: function(column) {
		return this._buildSummaryTypeOptionsFromOptionIds(
			['value', 'range'], column
		)
	},

	/** @override */
	getDataType: function(columnOrBreakdown) {
		if (columnOrBreakdown.get('breakdownBy')) {
			const datasetField = this.getDatasetField(columnOrBreakdown)
			return datasetField && datasetField.dataType
		} else {
			const dataType =
				ScorecardReportModel.prototype.getDataType.apply(this, arguments)
			if (dataType === DATASET_LINK) {
				return this.getDatasetField(columnOrBreakdown).dataType
			}
			return dataType
		}
	},

	/** @override */
	_getEveryXLabel: function(columnOrBreakdown) {
		if (columnOrBreakdown.get('breakdownBy')) {
			return this.getDatasetField(columnOrBreakdown).label
		}

		return columnOrBreakdown.get('label')
	},

	getDatasetField: function(columnOrBreakdown) {
		const attr = unwrapModel(columnOrBreakdown)
		const dataset = this.get('dataset')
		if (dataset) {
			const fieldId = attr.datasetFieldId || attr.breakdownBy
			return dataset.getUnderlyingField(fieldId)
		}
		return null
	},

	/** @override */
	_onChangeCalendarPeriod: function() {
		if (this.showPeriodSelector()) {
			this.reload()	//#39322
		}
	},

	hasCalendarFieldId: function() {
		return _.isFinite(_.parseInt(this.get('calendarFieldId')))
	},

	/**
	 * only show the period selector when an app calendar is set
	 * @override
	 */
	showPeriodSelector: function() {
		return this.hasCalendarFieldId() ||
			hasRelativeDateFilter(this.get('filters')) ||
			//if a breakdown is referencing a date field, we need to show a period selector
			_.find(this.stacks().models, stack => {
				let repeatBy = stack.get('repeatBy')
				if (!repeatBy) { return null }
				return _.find(repeatBy.get('breakdowns'), breakdown => {
					let field = this.getDatasetField(breakdown)
					return field && isDateTimeDataType(field.dataType)
				})
			}) != null
	},

	clone: function() {
		let newModel = ScorecardReportModel.prototype.clone.apply(this, arguments)
		newModel._fieldDataByDatasetFieldId = _.cloneDeep(this._fieldDataByDatasetFieldId)
		newModel.treeItemsById =_.cloneDeep(this.treeItemsById)
		newModel._liveFilters = _.cloneDeep(this._liveFilters)
		return newModel
	},

	hasEmptyDataset: function() {
		return _.isEmpty(this.columnOptionsList(null, {forColumn: true}))
	},

	hasColumnFilters: function() {
		return _.some(this.allColumns(), c =>
			!_.isEmpty(c.get('datasetFilters')))
	},

	fieldIdToOptionId(fieldId) {
		let dataset = this.get('dataset')
		let fieldData = this._fieldDataByDatasetFieldId[fieldId]
		return dataset.idsToFieldIdentifier(fieldData)
	},

	error: function() {
		let baseError = ScorecardReportModel.prototype.error.apply(this, arguments)
		if (!baseError) {
			let dataset = this.get('dataset')
			if (dataset && dataset.get('status') == 'error') {
				baseError = i18n.get('report.designer.dataset.error',
					`<a class="sp-navigate text-link" href="/datasets/${dataset.id}">
				${_.escape(dataset.get('label'))}</a>`)
			}
		}
		return baseError
	},

	requiresAggregationType: function(fieldIdOrColumnType, opts={}) {
		let requiresAggType = ScorecardReportModel.prototype.requiresAggregationType.apply(this, arguments)

		const column = opts.column ? unwrapModel(opts.column) : {}
		if (!_.isNil(column.groupingId)) return false

		let columnData = this._defaultColumnData(fieldIdOrColumnType)
		let defaultAggregationType = _.get(columnData, 'defaultAggregationType', null)
		let foreignFieldOnManySide = _.get(columnData, 'foreignFieldOnManySide', false)

		//#40816 show aggregation type when grouping/hiding individual records and
		//the target column isn't the one being grouped and agg type
		//also needed for datasets if it's a foriegn field on the many side where
		//the aggregation type is not none
		return requiresAggType || (foreignFieldOnManySide && defaultAggregationType != 'none')
	}

})

export default DatasetReportModel

DatasetReportModel.__filename__ = 'reports/dataset-report/dataset-report-model.js'
